import { useGetFundCategoryAverages } from '#/api/generated/category-averages/category-averages';
import type { FundCategoryAverages } from '#/api/generated/fINRAMarketDataAPI.schemas';
import { isSuccessResponse } from '#/lib/api-client';

/**
 * Morningstar category averages for a fund's `category_code`.
 *
 * The code only arrives with the fund's `/detail` response, so this is
 * necessarily a second request that stays idle until `categoryCode` resolves.
 * TanStack Query dedupes multiple requests, allowing the components to query
 */
export function useFundCategoryAverages(
  categoryCode: string | null | undefined,
): FundCategoryAverages | undefined {
  const query = useGetFundCategoryAverages(
    // The generated params type demands a string. `enabled` keeps the
    // placeholder from ever reaching the network.
    { category_code: categoryCode ?? '' },
    {
      query: {
        enabled: Boolean(categoryCode),
        // Category averages are aggregate figures that move ~daily at most, so
        // set long stale/gc windows (like the charts) to avoid revalidating on
        // every navigation and focus/reconnect event.
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  );

  return isSuccessResponse(query.data) ? query.data.data : undefined;
}
